import mixpanel from "mixpanel-browser";
import axiosInstance from "../../axiosInstance";
import { trackEvent } from "../../helperFunctions";
import URL from "../../urls";
import { CF_PROVIDER_ROLE, TYPE_PSYCHIATRIST, apiURL } from "../../variable";
import {
  ADD_TOAST,
  AUTOMATED_CALL_SUCCESS,
  CANCELLATION_REASON,
  CANCEL_UPCOMING_SESSION,
  CHANGE_MODE_SUCCESS,
  DAILY_SCHEDULE,
  FETCH_PROVIDER_PROFILE,
  FETCH_PROVIDER_SCHEDULE,
  FETCH_SESSIONS_LIST,
  MONTHLY_SCHEDULE,
  PROVIDER_MOBILE_VERIFIED,
  PROVIDER_PROFILE_LOADING,
  SESSIONS_LIST_LOADING,
  SESSION_COUNT,
  SESSION_RESCHEDULE_SUCCESS,
  UPDATE_SESSION_OBJ,
  EDIT_DAILY_SCHEDULE,
  BASE_WEEK_SCHEDULE,
  EDIT_BASE_WEEK_SCHEDULE,
  CLEAR_BASE_WEEK_SCHEDULE,
  UPDATE_LOCAL_PROVIDER_PROFILE,
  RESET_LOCAL_PROVIDER_PROFILE,
  UPDATE_REMOTE_PROVIDER_PROFILE,
  LOADING_SESSION_ACTIONS,
} from "./types";
import dayjs from "dayjs";
import Bugsnag from "@bugsnag/js";
import { getSlotsForAnalytics } from "../../utils/helper";
import { store } from "../configureStore";

export const fetchSessionsList = () => {
  return (dispatch, getState) => {
    dispatch({ type: SESSIONS_LIST_LOADING, payload: true });
    const usertype = getState().auth.user.usertype;
    const sessionsListURL =
      usertype === TYPE_PSYCHIATRIST
        ? URL.psychiatristSessionsList
        : URL.sessionsList;
    axiosInstance
      .get(sessionsListURL)
      .then(response => {
        dispatch({
          type: FETCH_SESSIONS_LIST,
          payload: response.data.bookings,
        });
        dispatch({ type: SESSIONS_LIST_LOADING, payload: false });
      })
      .catch(error => {
        console.log(error.response);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Error fetching Sessions List",
            appearance: "error",
            autoDismiss: false,
          },
        });
        dispatch({ type: SESSIONS_LIST_LOADING, payload: false });
      });
  };
};

export const getSessions = async payload => {
  const { isSupervisionMode, superviseeId, clientId, requestConfig } = payload;
  if (isSupervisionMode && !clientId) return;
  let requestUrl = isSupervisionMode
    ? `${apiURL}/supervisors/providers/${superviseeId}/clients/${clientId}/bookings`
    : URL.sessionsURL;
  try {
    const response = await axiosInstance.get(requestUrl, requestConfig);
    return response.data;
  } catch (error) {
    console.log(error.response);
  }
};

export const getAssessmentsAndGroupTherapyList = async params => {
  try {
    const response = await axiosInstance.get(
      `${URL.assessmentsAndGroupTherapy}`,
      params
    );
    return response.data;
  } catch (error) {
    console.log(error.response);
  }
};

export const getTransactions = async params => {
  try {
    const response = await axiosInstance.get(`${URL.transactions}`, params);
    return response.data;
  } catch (error) {
    console.log(error.response);
  }
};

export const cancelUserSession = (
  session,
  callback,
  analyticPayload,
  sessionCredit
) => {
  return (dispatch, getState) => {
    const usertype = getState().auth.user.usertype;
    const payload = {
      booking_id: session.id,
      session_cancel_by: usertype,
      session_credit: sessionCredit,
      cancellation_self_initiated: session.cancellation_self_initiated,
      modified_by: session.modified_by,
      long_reason: session.long_reason,
      short_reason: session.short_reason,
    };

    axiosInstance
      .post(URL.cancelSession, payload)
      .then(res => {
        if (res?.data?.booking?.status === "cancel") {
          dispatch({
            type: CANCEL_UPCOMING_SESSION,
            payload: { bookingID: res.data.booking.id },
          });
          dispatch({
            type: ADD_TOAST,
            payload: {
              msg: sessionCredit
                ? "The session was cancelled and the client was given 1 session credit."
                : "The session was cancelled successfully.",
            },
          });

          callback();
          if (analyticPayload) {
            trackEvent(analyticPayload);
          }
        }
      })
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `Session #${session.id} could not be cancelled. Please try again later.`,
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const approveUserSession = (
  session,
  callback,
  approved_status = "yes"
) => {
  return dispatch => {
    const payload = {
      booking_id: session.id,
      approved_status,
    };

    dispatch({ type: LOADING_SESSION_ACTIONS, payload: true });
    axiosInstance
      .post(URL.approveSession, payload)
      .then(res => {
        if (res.data.success && res.data.booking) {
          dispatch({
            type: UPDATE_SESSION_OBJ,
            payload: res.data.booking,
          });
          dispatch({
            type: ADD_TOAST,
            payload: {
              msg: `Session #${session.id} ${
                approved_status === "yes" ? "approved" : "rejected"
              } successfully.`,
            },
          });
          callback();
        }
      })
      .catch(err =>
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `Session #${session.id} could not be ${
              approved_status === "yes" ? "approved" : "rejected"
            }. Please try again later.`,
            appearance: "error",
            autoDismiss: false,
          },
        })
      )
      .finally(() => {
        dispatch({ type: LOADING_SESSION_ACTIONS, payload: false });
      });
  };
};

export const cancelNSP = (session, callback) => {
  if (!session?.id) return;
  return dispatch => {
    axiosInstance
      .delete(`${URL.nextSessionDelete}/${session?.id}`)
      .then(res => {
        if (res?.status === 200) {
          dispatch({
            type: CANCEL_UPCOMING_SESSION,
            payload: { bookingID: session?.id },
          });
          dispatch({
            type: ADD_TOAST,
            payload: {
              msg: `Next Session Prompt with Session id #${session.id} successfully retracted.`,
            },
          });
          callback();
        }
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `Next Session Prompt with Session id #${session.id} could not be retracted..`,
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const addSessionSummary = (
  booking_id,
  notes,
  attachment,
  callback,
  isPrivate,
  setLoadingState = () => {},
  groupId = null,
  isAIGenerated = false,
  aiResponseSelected = null
) => {
  return dispatch => {
    const { summary } = notes;
    const payload = {
      booking_id,
      summary,
      attachment,
      is_private: isPrivate,
    };
    if (groupId) payload.group_id = groupId;

    payload.ai_generated = isAIGenerated;
    payload.response_selected = aiResponseSelected || 1;

    axiosInstance
      .post(URL.addSessionSummary, payload)
      .then(res => {
        dispatch({
          type: ADD_TOAST,
          payload: { msg: `Session summary updated.` },
        });
        callback(booking_id, notes, attachment);
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Couldn't save session summary. Please try again.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      })
      .finally(() => setLoadingState(false));
  };
};

// makesCopy duplicates fetched profile data used to render and compare to original in profile section
export const fetchProfileData = (makeCopy = false, setLoading = false) => {
  return (dispatch, getState) => {
    dispatch({ type: PROVIDER_PROFILE_LOADING, payload: setLoading });
    const usertype = getState()?.auth?.user?.usertype;
    const profileDetailURL =
      usertype === TYPE_PSYCHIATRIST
        ? URL.psychiatristProfileDetail
        : URL.therapistProfileDetail;
    axiosInstance
      .get(profileDetailURL)
      .then(response => {
        let providerData = response.data[`${usertype}`];

        // If empty then these keys doesn't come in response hence adding empty array explicitly if undefined
        ["publications", "educations"]?.forEach(key => {
          if (!providerData?.[key]) {
            providerData[key] = [];
          }
        });

        dispatch({
          type: FETCH_PROVIDER_PROFILE,
          payload: providerData,
        });

        if (makeCopy) {
          dispatch({
            type: UPDATE_LOCAL_PROVIDER_PROFILE,
            payload: providerData,
          });
        }

        dispatch({ type: PROVIDER_PROFILE_LOADING, payload: false });
        const isCfProvider = providerData?.roles?.some(role =>
          CF_PROVIDER_ROLE.includes(role)
        );

        if (isCfProvider) {
          mixpanel.people.set_once("CF Provider", "true");
        } else {
          mixpanel.people.set_once("CF Provider", "false");
        }
      })
      .catch(error => {
        console.log(error.response);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch Provider Profile data. Please try again.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const updateLocalProviderProfile = data => {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_LOCAL_PROVIDER_PROFILE,
      payload: data,
    });
  };
};

export const updateRemoteProviderProfile = data => {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_REMOTE_PROVIDER_PROFILE,
      payload: data,
    });
  };
};

export const resetLocalProviderProfile = () => {
  return (dispatch, getState) => {
    dispatch({
      type: RESET_LOCAL_PROVIDER_PROFILE,
      payload: {},
    });
  };
};

export const fetchProviderSchedule = () => {
  return (dispatch, getState) => {
    axiosInstance
      .get(URL.getBaseWeekSchedules)
      .then(response => {
        dispatch({
          type: FETCH_PROVIDER_SCHEDULE,
          payload: response.data.data,
        });
      })
      .catch(error => {
        console.log(error.response);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch Provider schedule. Please try again.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const setProviderMobileVerified = isVerified => {
  return { type: PROVIDER_MOBILE_VERIFIED, payload: isVerified };
};

export const updateSessionMode = (session, typeofsession) => {
  return dispatch => {
    const payload = {
      booking_id: session.id,
      typeofsession,
    };

    axiosInstance
      .post(URL.updatebooking, payload)
      .then(res => {
        const updatedSessionObj = {
          ...session,
          typeofsession: res.data.booking.typeofsession,
        };
        dispatch({ type: UPDATE_SESSION_OBJ, payload: updatedSessionObj });
        dispatch({
          type: ADD_TOAST,
          payload: { msg: `Session mode updated.` },
        });
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Couldn't update session mode. Please try again.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const SessionModeChange = ({
  bookingId,
  payload,
  callback = () => {},
}) => {
  return dispatch => {
    axiosInstance
      .post(
        `${URL.sessionServiceChanges}/${bookingId}/change_session_mode`,
        payload
      )
      .then(res => {
        dispatch({ type: CHANGE_MODE_SUCCESS });
        dispatch({
          type: ADD_TOAST,
          payload: { msg: res.data.message },
        });
        if (typeof callback === "function") {
          callback();
        }
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Couldn't update session mode. Please try again.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const SessionReschedule = ({
  bookingId,
  payload,
  callback = () => {},
}) => {
  return dispatch => {
    return axiosInstance
      .post(
        `${URL.sessionServiceChanges}/${bookingId}/reschedule_session`,
        payload
      )
      .then(res => {
        dispatch({
          type: SESSION_RESCHEDULE_SUCCESS,
        });
        dispatch({
          type: ADD_TOAST,
          payload: { msg: res.data.message },
        });

        // trackEvent(analyticPayload);

        if (typeof callback === "function") {
          callback(res);
        }

        return res;
      })

      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Couldn't update session mode. Please try again.",
            appearance: "error",
            autoDismiss: false,
          },
        });
        throw err;
      });
  };
};

export const sessionCount = payload => {
  return dispatch => {
    axiosInstance
      .get(
        `${URL.sessionCount}?patient_id=${payload.user_id}&provider_id=${payload.provider_id}`
      )
      .then(res => {
        dispatch({
          type: SESSION_COUNT,
          payload: res.data,
        });
      })

      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Session count called failed",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const cancellationReason = payload => {
  return dispatch => {
    axiosInstance
      .post(URL.cancelSessionReason, payload)
      .then(res => {
        dispatch({
          type: CANCELLATION_REASON,
        });
      })

      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Couldn't Record reason of Cancellation",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const AutomatedCallService = ({ bookingId, analyticPayload }) => {
  return dispatch => {
    axiosInstance
      .get(`${URL.automatedCall}/${bookingId}`)
      .then(res => {
        dispatch({
          type: AUTOMATED_CALL_SUCCESS,
        });
        dispatch({
          type: ADD_TOAST,
          payload: { msg: "Automated call made successfully." },
        });
        trackEvent(analyticPayload);
      })
      .catch(err => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Couldn't make the automated call. Please try again.",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const getMonthlySchedules = payload => {
  // const { view_type,
  //     year,
  //     month,
  //     end_date,
  //     start_date}=payload;
  return dispatch => {
    dispatch({
      type: MONTHLY_SCHEDULE,
      payload: {
        loading: true,
        data: [],
      },
    });
    axiosInstance
      .post(`${URL.getMonthlySchedules}`, payload)
      .then(response => {
        dispatch({
          type: MONTHLY_SCHEDULE,
          payload: {
            loading: false,
            data: response.data.data,
          },
        });

        const date = new Date();
        date.setDate(1);
        date.setMonth(payload.month - 1);
        date.setFullYear(payload.year);

        trackEvent({
          event: "twa_availability_calendar_load",
          payload: { calendar_month: dayjs(date).format("MM-DD-YYYY") },
        });
      })
      .catch(error => {
        Bugsnag.notify(error);
        dispatch({
          type: MONTHLY_SCHEDULE,
          payload: {
            loading: false,
            data: [],
          },
        });
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch Provider's Monthly schedule!",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const getDailySchedules = (
  date,
  viaCTA,
  skipEvent = false,
  isCfProvider = false
) => {
  return dispatch => {
    dispatch({
      type: DAILY_SCHEDULE,
      payload: {
        loading: true,
        data: {
          date: "",
          online_slots: [],
          offline_slots: [],
          both_slots: [],
          booked_slots: [],
          blocked_slots: [],
        },
      },
    });

    let url = `${URL.getDateWiseSlots}?date=${date}`;
    if (isCfProvider) {
      url = `${url}&applicable_on=cf`;
    }
    axiosInstance
      .get(url)
      .then(response => {
        dispatch({
          type: DAILY_SCHEDULE,
          payload: {
            loading: false,
            data: response.data?.data?.days?.[0],
          },
        });

        const _date = new Date(date);

        if (!skipEvent) {
          trackCalendarDateSelectEvent(
            dayjs(_date).format("MM-DD-YYYY"),
            response.data?.data?.days?.[0],
            response.data?.data?.is_modified,
            viaCTA
          );
        }
      })
      .catch(error => {
        console.log(error.response);
        dispatch({
          type: DAILY_SCHEDULE,
          payload: {
            loading: false,
            data: {
              date: "",
              online_slots: [],
              offline_slots: [],
              both_slots: [],
              booked_slots: [],
              blocked_slots: [],
            },
          },
        });
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch Provider's Daily schedule for this date!",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

const trackCalendarDateSelectEvent = (
  calendar_date,
  slots,
  isModified,
  via_cta
) =>
  new Promise((resolve, reject) => {
    try {
      const finalSlots = getSlotsForAnalytics(slots);
      trackEvent({
        event: "twa_calendar_date_select",
        payload: {
          calendar_date,
          slots: finalSlots,
          is_modified: isModified,
          via_cta,
        },
      });
      resolve();
    } catch (error) {
      reject(error);
    }
  });

export const saveDailySchedule = (
  payload,
  localDailySchedule,
  isCfProvider = false
) => {
  return dispatch => {
    const { fromNavigation, ...rest } = payload;
    axiosInstance
      .post(URL.saveDateWiseSlots, rest)
      .then(response => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Schedule saved succesfully",
            appearance: "success",
            autoDismiss: true,
          },
        });
        //if fromNavigation is true, that means we have updated the slots and moved on to next datte,
        //so that means we we don't need to call the api for same date with updated slots
        if (!fromNavigation) {
          dispatch(getDailySchedules(payload.date, false, true, isCfProvider));
        }

        const slots = getSlotsForAnalytics(localDailySchedule);

        trackEvent({
          event: "twa_availability_save_success",
          payload: {
            calendar_date: dayjs(payload.date).format("MM-DD-YYYY"),
            slots,
            uploaded_by_admin: false,
            is_cf_provider: isCfProvider,
          },
        });
      })
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `Schedule save failed with status code ${error?.response?.status}`,
            appearance: "error",
          },
        });
      });
  };
};

export const editLocalSlots = payload => {
  return dispatch => {
    dispatch({
      type: EDIT_DAILY_SCHEDULE,
      payload: payload,
    });
  };
};

export const applyResetBaseWeek = date => {
  return dispatch => {
    axiosInstance
      .post(URL.resetToBaseWeek, {
        date: date,
      })
      .then(response => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Schedule reset to base week succesfully",
            appearance: "success",
            autoDismiss: true,
          },
        });
        dispatch(getDailySchedules(date));
      })
      .catch(error => {
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Error in resetting slots to base week",
            appearance: "error",
          },
        });
      });
  };
};

export const getProviderBaseWeekSchedule = (isCfProvider = false) => {
  return dispatch => {
    dispatch({
      type: BASE_WEEK_SCHEDULE,
      payload: {
        loading: true,
      },
    });
    axiosInstance
      .get(URL.getBaseWeekSchedules)
      .then(response => {
        dispatch({
          type: BASE_WEEK_SCHEDULE,
          payload: {
            loading: false,
            data: response.data.data,
          },
        });
        trackEvent({
          event: "twa_base_week_load",
          payload: {
            is_cf_provider: isCfProvider,
          },
        });
      })
      .catch(error => {
        dispatch({
          type: BASE_WEEK_SCHEDULE,
          payload: {
            loading: false,
          },
        });
        console.log(error.response);
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Could not fetch Provider base week schedule. Please try again!",
            appearance: "error",
            autoDismiss: false,
          },
        });
      });
  };
};

export const editBaseWeekSchedule = payload => {
  return dispatch => {
    dispatch({
      type: EDIT_BASE_WEEK_SCHEDULE,
      payload: payload,
    });
  };
};

export const clearBaseWeekSchedule = () => ({
  type: CLEAR_BASE_WEEK_SCHEDULE,
});

export const saveBaseweekSchedules = payload => {
  return dispatch => {
    dispatch({
      type: BASE_WEEK_SCHEDULE,
      payload: {
        loading: true,
      },
    });

    trackEvent({
      event: "twa_save_base_week_cta",
      payload: {
        // TODO: for later
        // slots: {
        //   online: payload?.online_schedules,
        //   offline: payload?.offline_schedules,
        //   both: payload?.both_schedules,
        // },
        uploaded_by_admin: false,
      },
    });

    axiosInstance
      .post(URL.saveBaseWeekSchedules, payload)
      .then(response => {
        dispatch({
          type: BASE_WEEK_SCHEDULE,
          payload: {
            loading: false,
          },
        });
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: "Schedule saved succesfully",
            appearance: "success",
            autoDismiss: true,
          },
        });

        dispatch(getProviderBaseWeekSchedule());
      })
      .catch(error => {
        dispatch({
          type: BASE_WEEK_SCHEDULE,
          payload: {
            loading: false,
          },
        });
        dispatch({
          type: ADD_TOAST,
          payload: {
            msg: `Schedule save failed with status code ${error?.response?.status}`,
            appearance: "error",
          },
        });
      });
  };
};
